import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { login } from './authService';
import { LoginBackdrop } from './LoginBackdrop';

/**
 * Full-screen centred login. Pixel-perfect card (380px) over a decorative
 * backdrop. Brand green here is #16a37b (the app green) — NOT --s-accent.
 *
 * Las credenciales del formulario se envían AL BACKEND
 * (`POST /api/autenticacion/login`), que wrapea Keycloak: el usuario nunca
 * abandona esta pantalla ni ve el IAM. En modo `mock` entra directo.
 */
export function LoginScreen() {
  const navigate = useNavigate();
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(user, pass);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión.');
      setBusy(false);
    }
  }

  return (
    <div
      className="relative grid h-screen place-items-center overflow-hidden"
      style={{ background: '#eef1ef' }}
    >
      <LoginBackdrop />

      {/* CARD */}
      <form
        onSubmit={onSubmit}
        className="relative z-10 w-[380px] animate-floatY overflow-hidden rounded-[14px] shadow-loginCard backdrop-blur-[10px]"
        style={{
          background: 'rgba(255,255,255,.92)',
          border: '1px solid rgba(20,40,32,.10)',
        }}
      >
        {/* top gradient bar */}
        <div
          className="h-[3px]"
          style={{
            background:
              'linear-gradient(90deg,transparent,#16a37b,#3aa0ff,transparent)',
          }}
        />

        {/* header */}
        <div
          className="flex items-center gap-[10px] px-[28px] pb-[18px] pt-[26px]"
          style={{ borderBottom: '1px solid #e7ece9' }}
        >
          <div className="grid h-[34px] w-[34px] place-items-center rounded-[8px] bg-login font-mono text-[12px] font-extrabold tracking-[.5px] text-white">
            C4T
          </div>
          <div className="min-w-0 leading-[1.25]">
            <div className="text-[15px] font-extrabold tracking-[1.5px] text-[#16241d]">
              C4T AMBULANCIAS
            </div>
            <div className="mt-[2px] font-mono text-[9.5px] tracking-[.5px] text-[#6b7a72]">
              SEGURIDAD CIVIL · EMERGENCIAS
            </div>
          </div>
        </div>

        {/* body */}
        <div className="px-[28px] pb-[28px] pt-[24px]">
          <label className="mb-[6px] block font-mono text-[9px] uppercase tracking-[1px] text-[#8a978f]">
            Usuario
          </label>
          <Input
            className="mb-[16px]"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
          />

          <label className="mb-[6px] block font-mono text-[9px] uppercase tracking-[1px] text-[#8a978f]">
            Contraseña
          </label>
          <Input
            type="password"
            className="mb-[22px]"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoComplete="current-password"
          />

          {error && (
            <div
              role="alert"
              className="mb-[14px] rounded-[8px] px-[12px] py-[9px] text-[12px] font-medium"
              style={{ background: '#fdecec', color: '#b3261e', border: '1px solid #f5c6c2' }}
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="login"
            size="login"
            disabled={busy}
            className="tracking-[.3px]"
          >
            {busy ? 'Conectando…' : 'Entrar al servicio'}
          </Button>

          <div className="mt-[16px] flex justify-between font-mono text-[9.5px] text-[#8a978f]">
            <span>v0.9 · wireframe</span>
            <a href="#" className="hover:text-[#16241d]">
              ¿Has olvidado la contraseña?
            </a>
          </div>
        </div>
      </form>
    </div>
  );
}
